/**
 * PostHog + membership alignment for **this brand repo** (Ana Fernández).
 *
 * Canonical project: **422815** (Ana Fernandez) — locked for the entire project.
 * Never use 297103 (Clari Moine), 421574 (Katya), or other brand project IDs.
 *
 * @see api/lib/posthogBrandAlignment.ts (server twin — keep in sync)
 * @see https://us.posthog.com/project/422815
 */

import { BRAND_SOURCE_KEY, BRAND_SITE_HOST } from "@/lib/brandConfig";

/** Numeric PostHog project id — Ana Fernandez only. Locked. */
export const BRAND_POSTHOG_PROJECT_ID = "422815";

export const BRAND_POSTHOG_PROJECT_NAME = "Ana Fernandez";

export const BRAND_POSTHOG_UI_HOST = "https://us.posthog.com";

export const BRAND_POSTHOG_PROJECT_URL = `${BRAND_POSTHOG_UI_HOST}/project/${BRAND_POSTHOG_PROJECT_ID}`;

/**
 * Primary paid product: VIP **membership** (Stripe subscription via DualCTA).
 * Distinct from `/vip/:slug` one-time gift donation.
 */
export const MEMBERSHIP_PRODUCT = {
  plan_tier: "vip" as const,
  product_type: "membership" as const,
  surfaces: ["dual_cta", "premium_invite", "bio_premium"] as const,
} as const;

/**
 * Funnel events aligned with PostHog 422815 Customer analytics.
 * DualCTA also emits Slack aliases (cta_click_vip, dual_cta_video_started);
 * both names are dual-fired so Customer analytics + ops stay perfect.
 */
export const MEMBERSHIP_FUNNEL_EVENTS = {
  activity: "dual_cta_video_viewed",
  intent: "dual_cta_video_premium_click",
  checkout_created: "vip_membership_checkout_created",
  payment_success: "vip_membership_payment_success",
  /** Ops / Slack aliases (still dual-fired) */
  intent_alias: "cta_click_vip",
  activity_alias: "dual_cta_video_started",
  checkout_initiated: "vip_membership_checkout_initiated",
} as const;

/** `/vip/:slug` one-time gift — not membership. */
export const VIP_CUSTOM_DONATION = {
  pack_id: "sorpresa",
  product_type: "vip_custom",
  flow_name: "vip_custom_donation",
  surface: "vip_custom_page",
} as const;

/**
 * Always **422815** for this brand SPA.
 * Env values other than 422815 are ignored (cannot point at foreign brands).
 */
export function resolveClientPostHogProjectId(): string {
  return BRAND_POSTHOG_PROJECT_ID;
}

/** Super properties for every browser event. */
export function brandPostHogIdentityProps(): Record<string, string | boolean | null> {
  return {
    site: BRAND_SOURCE_KEY,
    brand: BRAND_SOURCE_KEY,
    brand_host: BRAND_SITE_HOST,
    posthog_project_id: BRAND_POSTHOG_PROJECT_ID,
    primary_product: MEMBERSHIP_PRODUCT.plan_tier,
    primary_product_type: MEMBERSHIP_PRODUCT.product_type,
  };
}

/**
 * True for real PostHog `$session_id` values used by session replay.
 *
 * posthog-js emits time-ordered **UUID v7** (version nibble `7`), e.g.
 * `019fc73e-ee0d-759d-b184-8754d8b90cfe`.
 *
 * `crypto.randomUUID()` is **UUID v4** (nibble `4`) — DualCTA used those as
 * `session_id` / fake `$session_id`, then built `/replay/{v4}` links that 404.
 * Never treat v4 funnel IDs as replay ids.
 */
export function isLikelyPostHogSessionId(sessionId: string | null | undefined): boolean {
  const s = String(sessionId ?? "").trim();
  if (!s || s.length < 16 || s.startsWith("cs_")) return false;
  // UUID v7 (PostHog recording / $session_id)
  if (
    /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      s,
    )
  ) {
    return true;
  }
  // Opaque 32-hex (legacy / alternate) — not UUID v4
  if (/^[0-9a-f]{32}$/i.test(s) && !/^[0-9a-f]{12}4[0-9a-f]{19}$/i.test(s)) {
    return true;
  }
  return false;
}

/** True only for us.posthog.com/project/422815/replay/{real-ph-session}/… */
export function isBrandPostHogReplayUrl(url: string | null | undefined): boolean {
  const u = String(url ?? "").trim();
  if (!u.startsWith("https://")) return false;
  if (u.includes("/ingest") || u.includes("/project/phc_")) return false;
  if (!u.includes(`/project/${BRAND_POSTHOG_PROJECT_ID}/replay/`)) return false;
  const m = u.match(/\/replay\/([^/?#]+)/);
  if (!m?.[1]) return false;
  try {
    return isLikelyPostHogSessionId(decodeURIComponent(m[1]));
  } catch {
    return false;
  }
}
